import {
  Box,
  Checkbox,
  Flex,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Text
} from '@chakra-ui/react'

import { FormField } from '../../../components/FormField'
import { LoadingBlock } from '../../../components/Loading'
import { MoneyInput } from '../../../components/MaskedInput'
import { SearchableSelect } from '../../../components/Select'
import { TBulkMode } from '../../../services/CoursesService/interfaces'

interface IBulkPriceFormProps {
  mode: TBulkMode

  // Course selection
  courses: { id: number; name: string }[]
  loadingCourses: boolean
  selectedCourseIds: number[]
  onCourseIdsChange: (ids: number[]) => void
  coursePlaceholder?: string

  // Price fields
  discount: string
  onDiscountChange: (val: string) => void
  taxaMatricula: number | null
  onTaxaMatriculaChange: (val: number | null) => void
  priceOriginal: number | null
  onPriceOriginalChange: (val: number | null) => void

  // Coupon fields
  removeCoupon: boolean
  onRemoveCouponChange: (checked: boolean) => void
  couponValue: string
  onCouponValueChange: (val: string) => void

  // CTA text
  ctaText: string
  onCtaTextChange: (val: string) => void

  /** Render the course select inline (true for EditBulkScheduleModal) */
  compact?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function BulkPriceForm({
  mode,
  courses,
  loadingCourses,
  selectedCourseIds,
  onCourseIdsChange,
  coursePlaceholder = 'Selecione os cursos…',
  discount,
  onDiscountChange,
  taxaMatricula,
  onTaxaMatriculaChange,
  priceOriginal,
  onPriceOriginalChange,
  removeCoupon,
  onRemoveCouponChange,
  couponValue,
  onCouponValueChange,
  ctaText,
  onCtaTextChange,
  compact = false
}: IBulkPriceFormProps) {
  const selectOptions = courses.map((c) => ({ value: c.id, label: c.name }))

  const bgColor = compact ? undefined : 'gray.50'
  const boxProps = compact ? {} : { bg: bgColor, borderRadius: 'md', p: 4 }

  return (
    <>
      {/* Course selection */}
      <FormField label="Cursos" mb={5}>
        {loadingCourses ? (
          <LoadingBlock entity="cursos" py={2} />
        ) : (
          <SearchableSelect<number>
            isMulti
            placeholder={coursePlaceholder}
            entity="curso"
            options={selectOptions}
            value={selectedCourseIds}
            onChange={onCourseIdsChange}
          />
        )}
      </FormField>

      {/* Discount mode */}
      {mode === 'discount' && (
        <Box {...boxProps}>
          <FormField label="Desconto (%)" maxW="200px">
            <NumberInput min={0} max={100} value={discount} onChange={onDiscountChange} precision={2}>
              <NumberInputField placeholder="Ex: 15" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormField>
          {!compact && discount !== '' && (
            <Text variant="caption" mt={2}>
              O preço original de cada curso será mantido e receberá {discount}% de desconto.
            </Text>
          )}
        </Box>
      )}

      {/* Prices mode */}
      {mode === 'prices' && (
        <Box {...boxProps}>
          {compact ? (
            <Flex gap={2} flexWrap="wrap">
              <FormField label="Taxa de Matrícula" flex="1" minW="140px">
                <MoneyInput value={taxaMatricula} onChange={onTaxaMatriculaChange} placeholder="Opcional" />
              </FormField>
              <FormField label="Preço Original" flex="1" minW="140px">
                <MoneyInput value={priceOriginal} onChange={onPriceOriginalChange} placeholder="Opcional" />
              </FormField>
              <FormField label="Desconto (%)" flex="1" minW="140px">
                <NumberInput min={0} max={100} value={discount} onChange={onDiscountChange} precision={2}>
                  <NumberInputField placeholder="Opcional" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormField>
            </Flex>
          ) : (
            <>
              <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
                <FormField label="Taxa de Matrícula">
                  <MoneyInput value={taxaMatricula} onChange={onTaxaMatriculaChange} placeholder="Opcional" />
                </FormField>
                <FormField label="Preço Original">
                  <MoneyInput value={priceOriginal} onChange={onPriceOriginalChange} placeholder="Opcional" />
                </FormField>
                <FormField label="Desconto (%)">
                  <NumberInput min={0} max={100} value={discount} onChange={onDiscountChange} precision={2}>
                    <NumberInputField placeholder="Opcional" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormField>
              </Stack>
              <Text variant="caption" mt={3}>
                Campos deixados em branco mantêm os valores atuais de cada curso.
              </Text>
            </>
          )}
        </Box>
      )}

      {/* Coupon mode */}
      {mode === 'coupon' && (
        <Box {...boxProps}>
          <Checkbox
            isChecked={removeCoupon}
            onChange={(e) => {
              onRemoveCouponChange(e.target.checked)
              if (e.target.checked) onCouponValueChange('')
            }}
            mb={3}
          >
            <Text fontSize="sm">Remover cupons</Text>
          </Checkbox>
          <FormField label="Cupom" isDisabled={removeCoupon}>
            <Input
              placeholder="Ex: PROMO2026"
              value={couponValue}
              onChange={(e) => onCouponValueChange(e.target.value.toUpperCase())}
              isDisabled={removeCoupon}
              maxLength={100}
            />
          </FormField>
          {!compact && (
            <Text variant="caption" mt={3}>
              {removeCoupon
                ? 'O parâmetro cupom será removido da URL da Eduzz de todos os cursos selecionados.'
                : 'O cupom será adicionado (ou substituído) no campo eduzz_url de todos os cursos selecionados.'}
            </Text>
          )}
        </Box>
      )}

      {/* CTA text */}
      <Box {...(compact ? { mt: 5 } : { ...boxProps, mt: 5 })}>
        <FormField label="Texto CTA (opcional)">
          <Input
            placeholder="Ex: Matricule-se agora!"
            value={ctaText}
            onChange={(e) => onCtaTextChange(e.target.value)}
            maxLength={255}
          />
        </FormField>
        <Text variant="caption" mt={compact ? 1 : 2}>
          Se preenchido, o texto CTA será atualizado {compact ? 'nos cursos' : 'em todos os cursos selecionados'}. Se
          vazio, mantém o valor atual.
        </Text>
      </Box>
    </>
  )
}
